import { useMemo, useState } from 'react';
import { KeyboardAccessibility } from '../../../utils/KeyboardAccessibility';
import { TextInput } from './Input';

interface Props<T extends { [key: string]: string }> {
    options: T;
    activeKey: keyof T;
    onChange: (option: keyof T) => void;
    invalid?: boolean;
    zIndex?: number;
    height?: string;
    id: string;
}

export default function Select<T extends { [key: string]: string }>({
    options,
    activeKey,
    onChange,
    invalid = false,
    height,
    id,
}: Props<T>) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const filteredOptions = useMemo(() => {
        if (filter === '') return options;
        const lowerFilter = filter.toLowerCase();
        return Object.fromEntries(Object.entries(options).filter((o) => o[1].toLowerCase().includes(lowerFilter)));
    }, [filter, options]);

    function toggleMenu() {
        setOpen((prev) => !prev);
    }

    const handleChange = (option: keyof T) => {
        onChange(option);
        setOpen(false);
    };

    return (
        <div className='relative'>
            <p
                tabIndex={0}
                onClick={toggleMenu}
                onKeyDown={KeyboardAccessibility.onSelect(toggleMenu)}
                id={id}
                className={
                    'cursor-pointer bg-theme-950/70 border border-theme-outline round:rounded-lg w-full h-9 px-3 pe-8 flex items-center transition-all focus-within:border-theme-primary focus-within:ring-2 focus-within:ring-theme-primary/20 overflow-hidden' +
                    (invalid ? ' border-red-600 bg-red-950/10 focus-within:ring-red-600/20' : '')
                }
            >
                {options[activeKey]}
            </p>
            <i className='bx bx-caret-down absolute top-1/2 right-3 -translate-y-1/2 pointer-events-none' />
            {open && <div className='fixed inset-0 z-10' onClick={() => setOpen(false)} />}
            <div
                className={
                    'absolute z-10 left-0 right-0 top-full mt-1 transition-opacity ' +
                    (open ? 'opacity-100' : 'opacity-0 pointer-events-none')
                }
            >
                <ul
                    className={`bg-theme-700 border border-theme-outline shadow-2xl round:rounded-xl max-h-${height ?? '44'} overflow-y-auto scrollbar-thin`}
                    style={{ maxHeight: height ? `${(parseInt(height) / 4).toFixed(2)}rem` : '11rem' }}
                >
                    {Object.entries(options).length > 5 && (
                        <li className='p-1'>
                            <TextInput
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder='Filter...'
                            />
                        </li>
                    )}
                    {Object.entries(filteredOptions).map((o) => (
                        <SelectOption option={o} setValue={handleChange} key={o[0] + '_0'} />
                    ))}
                </ul>
            </div>
        </div>
    );
}

function SelectOption<T>({ option, setValue }: { option: [T, string]; setValue: (option: T) => void }) {
    function handleClick() {
        setValue(option[0]);
    }

    return (
        <li className='hover:bg-theme-600 px-3 py-2'>
            <button className='w-full text-left' type='button' onClick={handleClick}>
                {option[1]}
            </button>
        </li>
    );
}
